/**
 * Этап 5 / KS-02 — the «Клиенты» section: clients → their contacts → linked projects,
 * plus the duplicates report and merge.
 *
 * ── Why this section exists at all.
 * Contacts are extracted from inbound email by an LLM, so the directory accumulates
 * near-identical rows on its own (the live database already carried the same address
 * twice). A picker over a directory nobody can correct degrades back into "type the
 * contact in again" — the complaint KS-02 answers. So the directory needs a place to
 * rename, re-assign, delete and MERGE.
 *
 * ── Two panes, not a modal stack.
 * Left: the client list. Right: the selected client with its contacts and projects.
 * The selection is in the URL (`/clients/7`), so a colleague can be sent a link to the
 * row under discussion.
 *
 * ── Merge is two clicks by design.
 * The first call previews (writes nothing) and the second performs — the project's
 * convention for a destructive step. The preview is shown verbatim, including which
 * fields the survivor will GAIN, because "what did I just lose" is unanswerable
 * afterwards.
 *
 * ── Single-row deletes ask first (E12-F02-T02).
 * The server only refuses a row still referenced by a project (409). A client or contact
 * with no projects — e.g. one the LLM has just extracted — would otherwise vanish on one
 * click, irreversibly. So both «Delete» buttons go through the styled `useConfirm`
 * dialog that names the record; no DELETE leaves before the user confirms.
 */
import { useState, type JSX } from 'react';
import type {
  ClientDTO,
  ContactDTO,
  DuplicateGroupDTO,
  DuplicateMemberDTO,
  MergePreviewDTO,
} from '@eer/shared';
import { errorMessage } from '../api/client.js';
import { useToast } from '../ui/ToastContext.js';
import { Dialog, DialogFooter } from '../ui/Dialog.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import {
  useClientDetail,
  useCreateClient,
  useCreateContact,
  useDeleteClient,
  useDeleteContact,
  useDirectory,
  useDuplicates,
  useMerge,
  useUpdateClient,
  useUpdateContact,
} from './directoryApi.js';
import { contactLabel } from './projectContactsModel.js';

const BTN_PRIMARY =
  'rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50';
const BTN_GHOST =
  'rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm disabled:opacity-50';
const BTN_DANGER =
  'rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 disabled:opacity-50';
const INPUT =
  'w-full rounded-md border border-[var(--color-border)] p-2 text-sm outline-none focus:border-[var(--color-accent)]';

export interface ClientsViewProps {
  /** Selected client id from the route (`/clients/:id`), or null for the list only. */
  readonly selectedId: number | null;
  readonly onSelect: (clientId: number | null) => void;
}

export function ClientsView({ selectedId, onSelect }: ClientsViewProps): JSX.Element {
  const [tab, setTab] = useState<'directory' | 'duplicates'>('directory');
  const directory = useDirectory();

  return (
    <div data-testid="clients-view" className="space-y-4">
      <nav className="flex gap-2" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'directory'}
          className={tab === 'directory' ? BTN_PRIMARY : BTN_GHOST}
          data-testid="clients-tab-directory"
          onClick={() => setTab('directory')}
        >
          Directory
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'duplicates'}
          className={tab === 'duplicates' ? BTN_PRIMARY : BTN_GHOST}
          data-testid="clients-tab-duplicates"
          onClick={() => setTab('duplicates')}
        >
          Duplicates
        </button>
      </nav>

      {tab === 'duplicates' ? (
        <DuplicatesPanel />
      ) : directory.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : directory.isError ? (
        <p data-testid="clients-error" className="text-sm text-red-700">
          {errorMessage(directory.error, 'Could not load the directory')}
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <ClientList
            clients={directory.data?.clients ?? []}
            unassigned={directory.data?.unassigned_contacts ?? []}
            selectedId={selectedId}
            onSelect={onSelect}
          />
          {selectedId === null ? (
            <p data-testid="clients-no-selection" className="text-sm text-slate-500">
              Select a client on the left.
            </p>
          ) : (
            <ClientDetailPane clientId={selectedId} onDeleted={() => onSelect(null)} />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Left pane — the client list
// ---------------------------------------------------------------------------

interface ClientListProps {
  readonly clients: readonly ClientDTO[];
  readonly unassigned: readonly ContactDTO[];
  readonly selectedId: number | null;
  readonly onSelect: (id: number | null) => void;
}

function ClientList({ clients, unassigned, selectedId, onSelect }: ClientListProps): JSX.Element {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [newName, setNewName] = useState('');
  const createClient = useCreateClient();

  const needle = query.trim().toLowerCase();
  const shown = needle ? clients.filter((c) => c.name.toLowerCase().includes(needle)) : clients;

  async function add(): Promise<void> {
    const name = newName.trim();
    if (!name) return;
    try {
      const created = await createClient.mutateAsync({ name });
      setNewName('');
      onSelect(created.id);
      toast.success('Client created');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not create the client'));
    }
  }

  return (
    <aside className="space-y-3">
      <input
        className={INPUT}
        placeholder="Search by name"
        value={query}
        data-testid="clients-search"
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul
        data-testid="clients-list"
        className="divide-y divide-[var(--color-border)] rounded-lg border border-[var(--color-border)] bg-white"
      >
        {shown.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              aria-current={c.id === selectedId}
              data-testid={`client-row-${c.id}`}
              className={`flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left text-sm ${
                c.id === selectedId ? 'bg-slate-100 font-medium' : ''
              }`}
              onClick={() => onSelect(c.id)}
            >
              <span>{c.name}</span>
              <span className="whitespace-nowrap text-xs text-slate-500">
                {c.contacts_count} contacts · {c.projects_count} projects
              </span>
            </button>
          </li>
        ))}
        {shown.length === 0 ? (
          <li className="px-3 py-2 text-sm text-slate-500">Nothing found.</li>
        ) : null}
      </ul>

      <div className="flex gap-2">
        <input
          className={INPUT}
          placeholder="New client"
          value={newName}
          data-testid="clients-new-name"
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          type="button"
          className={BTN_PRIMARY}
          disabled={!newName.trim() || createClient.isPending}
          data-testid="clients-new-save"
          onClick={() => void add()}
        >
          +
        </button>
      </div>

      {unassigned.length > 0 ? (
        <section
          data-testid="clients-unassigned"
          className="rounded-lg border border-dashed border-[var(--color-border)] p-3"
        >
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Contacts without a client
          </h4>
          {/* These exist by design: a contact extracted from an inbound email persists
              even when no client could be resolved. Without this list they would be
              unreachable — invisible to the picker AND to any correction. */}
          <ul className="mt-2 space-y-1 text-sm">
            {unassigned.map((c) => (
              <li key={c.id} data-testid={`unassigned-contact-${c.id}`}>
                {contactLabel(c)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Right pane — one client
// ---------------------------------------------------------------------------

function ClientDetailPane({
  clientId,
  onDeleted,
}: {
  readonly clientId: number;
  readonly onDeleted: () => void;
}): JSX.Element {
  const toast = useToast();
  const detail = useClientDetail(clientId);
  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();
  const createContact = useCreateContact();
  const { confirm, confirmDialog } = useConfirm();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', domain: '', website: '', industry: '' });
  const [newContact, setNewContact] = useState({ name: '', email: '', position: '' });

  const client = detail.data?.client;

  function openEdit(): void {
    if (!client) return;
    setForm({
      name: client.name,
      domain: client.domain ?? '',
      website: client.website ?? '',
      industry: client.industry ?? '',
    });
    setEditing(true);
  }

  async function saveClient(): Promise<void> {
    try {
      await updateClient.mutateAsync({
        id: clientId,
        patch: {
          name: form.name,
          domain: form.domain,
          website: form.website,
          industry: form.industry,
        },
      });
      setEditing(false);
      toast.success('Client saved');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the client'));
    }
  }

  async function removeClient(): Promise<void> {
    const ok = await confirm({
      title: 'Delete client?',
      message: `Delete client ${client?.name.trim() || 'this client'}? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
      testId: 'client-delete-confirm',
    });
    if (!ok) return;
    try {
      await deleteClient.mutateAsync(clientId);
      toast.success('Client deleted');
      onDeleted();
    } catch (error) {
      // A 409 carries the number of projects still referencing the row — the only
      // answer a human can act on, so it is shown verbatim rather than replaced.
      toast.error(errorMessage(error, 'Could not delete the client'));
    }
  }

  async function addContact(): Promise<void> {
    const name = newContact.name.trim();
    if (!name) return;
    try {
      await createContact.mutateAsync({
        client_id: clientId,
        name,
        email: newContact.email.trim() || null,
        position: newContact.position.trim() || null,
      });
      setNewContact({ name: '', email: '', position: '' });
      toast.success('Contact added');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not add the contact'));
    }
  }

  if (detail.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!client) {
    return (
      <p data-testid="client-detail-missing" className="text-sm text-slate-500">
        Client not found.
      </p>
    );
  }

  return (
    <section data-testid="client-detail" className="space-y-4">
      <header className="rounded-lg border border-[var(--color-border)] bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 data-testid="client-detail-name" className="text-lg font-semibold">
              {client.name}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              {[client.domain, client.industry, client.website].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className={BTN_GHOST}
              data-testid="client-edit"
              onClick={openEdit}
            >
              Edit
            </button>
            <button
              type="button"
              className={BTN_DANGER}
              disabled={deleteClient.isPending}
              data-testid="client-delete"
              onClick={() => void removeClient()}
            >
              Delete
            </button>
          </div>
        </div>
      </header>

      <section className="rounded-lg border border-[var(--color-border)] bg-white p-4">
        <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-700">
          Contacts
        </h4>
        <ul data-testid="client-contacts" className="divide-y divide-[var(--color-border)]">
          {(detail.data?.contacts ?? []).map((c) => (
            <ContactRow key={c.id} contact={c} />
          ))}
          {(detail.data?.contacts ?? []).length === 0 ? (
            <li className="py-2 text-sm text-slate-500">No contacts yet.</li>
          ) : null}
        </ul>

        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          <input
            className={INPUT}
            placeholder="Name"
            value={newContact.name}
            data-testid="client-new-contact-name"
            onChange={(e) => setNewContact((s) => ({ ...s, name: e.target.value }))}
          />
          <input
            className={INPUT}
            placeholder="Email"
            value={newContact.email}
            data-testid="client-new-contact-email"
            onChange={(e) => setNewContact((s) => ({ ...s, email: e.target.value }))}
          />
          <input
            className={INPUT}
            placeholder="Position"
            value={newContact.position}
            onChange={(e) => setNewContact((s) => ({ ...s, position: e.target.value }))}
          />
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={!newContact.name.trim() || createContact.isPending}
            data-testid="client-new-contact-save"
            onClick={() => void addContact()}
          >
            Add
          </button>
        </div>
      </section>

      <section className="rounded-lg border border-[var(--color-border)] bg-white p-4">
        <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-700">
          Projects
        </h4>
        <ul data-testid="client-projects" className="space-y-1 text-sm">
          {(detail.data?.projects ?? []).map((p) => (
            <li key={p.id}>
              #{p.id} — {p.role_name} <span className="text-xs text-slate-500">({p.status})</span>
            </li>
          ))}
          {(detail.data?.projects ?? []).length === 0 ? (
            <li className="text-slate-500">No projects.</li>
          ) : null}
        </ul>
      </section>

      {editing ? (
        <Dialog open onClose={() => setEditing(false)} title="Client" testId="client-edit-dialog">
          <div className="grid gap-2">
            {(['name', 'domain', 'website', 'industry'] as const).map((field) => (
              <label key={field} className="text-xs text-slate-600">
                {field}
                <input
                  className={INPUT}
                  value={form[field]}
                  data-testid={`client-edit-${field}`}
                  onChange={(e) => setForm((s) => ({ ...s, [field]: e.target.value }))}
                />
              </label>
            ))}
          </div>
          <DialogFooter
            onCancel={() => setEditing(false)}
            onConfirm={() => void saveClient()}
            cancelLabel="Cancel"
            confirmLabel="Save"
            confirmDisabled={updateClient.isPending || form.name.trim().length === 0}
            testId="client-edit"
          />
        </Dialog>
      ) : null}
      {confirmDialog}
    </section>
  );
}

function ContactRow({ contact }: { readonly contact: ContactDTO }): JSX.Element {
  const toast = useToast();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const { confirm, confirmDialog } = useConfirm();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: contact.name,
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    position: contact.position ?? '',
  });

  async function save(): Promise<void> {
    try {
      await updateContact.mutateAsync({
        id: contact.id,
        patch: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          position: form.position,
        },
      });
      setEditing(false);
      toast.success('Contact saved');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the contact'));
    }
  }

  async function remove(): Promise<void> {
    const ok = await confirm({
      title: 'Delete contact?',
      message: `Delete contact ${contact.name.trim() || 'this contact'}? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
      testId: 'contact-delete-confirm',
    });
    if (!ok) return;
    try {
      await deleteContact.mutateAsync(contact.id);
      toast.success('Contact deleted');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not delete the contact'));
    }
  }

  return (
    <li
      className="flex items-center justify-between gap-3 py-2 text-sm"
      data-testid={`contact-row-${contact.id}`}
    >
      {editing ? (
        <div className="grid flex-1 gap-2 sm:grid-cols-4">
          {(['name', 'email', 'phone', 'position'] as const).map((field) => (
            <input
              key={field}
              className={INPUT}
              value={form[field]}
              placeholder={field}
              data-testid={`contact-edit-${contact.id}-${field}`}
              onChange={(e) => setForm((s) => ({ ...s, [field]: e.target.value }))}
            />
          ))}
        </div>
      ) : (
        <span>
          {contactLabel(contact)}
          {contact.projects_count > 0 ? (
            <span className="ml-2 text-xs text-slate-500">
              in {contact.projects_count} project(s)
            </span>
          ) : null}
        </span>
      )}
      <span className="flex shrink-0 gap-2">
        {editing ? (
          <>
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={updateContact.isPending}
              data-testid={`contact-save-${contact.id}`}
              onClick={() => void save()}
            >
              OK
            </button>
            <button type="button" className={BTN_GHOST} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={BTN_GHOST}
              data-testid={`contact-edit-${contact.id}`}
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
            <button
              type="button"
              className={BTN_DANGER}
              disabled={deleteContact.isPending}
              data-testid={`contact-delete-${contact.id}`}
              onClick={() => void remove()}
            >
              Delete
            </button>
          </>
        )}
      </span>
      {confirmDialog}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Duplicates + merge
// ---------------------------------------------------------------------------

function DuplicatesPanel(): JSX.Element {
  const duplicates = useDuplicates();

  if (duplicates.isLoading)
    return <p className="text-sm text-slate-500">Looking for duplicates…</p>;
  const clients = duplicates.data?.clients ?? [];
  const contacts = duplicates.data?.contacts ?? [];

  if (clients.length === 0 && contacts.length === 0) {
    return (
      <p data-testid="duplicates-none" className="text-sm text-slate-500">
        No duplicates found.
      </p>
    );
  }

  return (
    <div data-testid="duplicates-panel" className="space-y-4">
      {contacts.length > 0 ? (
        <section>
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-700">
            Contacts
          </h4>
          <ul className="space-y-2">
            {contacts.map((g) => (
              <DuplicateGroup key={`c-${g.reason}-${g.value}`} kind="contacts" group={g} />
            ))}
          </ul>
        </section>
      ) : null}
      {clients.length > 0 ? (
        <section>
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-700">
            Clients
          </h4>
          <ul className="space-y-2">
            {clients.map((g) => (
              <DuplicateGroup key={`cl-${g.reason}-${g.value}`} kind="clients" group={g} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Why a group was proposed — the two rules carry very different confidence. */
const REASON_LABEL: Record<DuplicateGroupDTO['reason'], string> = {
  same_email: 'same email — almost certainly one record',
  same_name: 'same name — a hint only, please check',
};

/**
 * What the human needs to tell two rows apart (E12-F17-T08): the name AS STORED (so
 * «Amit  Kedar» with its double space is visibly the odd one), then the domain for a
 * client, or email + owning client for a contact. Falls back to `#id` if the backend
 * sent no member for this id.
 */
function memberLabel(
  kind: 'clients' | 'contacts',
  members: readonly DuplicateMemberDTO[],
  id: number,
): string {
  const m = members.find((x) => x.id === id);
  if (!m) return `#${id}`;
  const details =
    kind === 'clients'
      ? [m.domain ?? 'no domain']
      : [m.email ?? 'no email', m.client_name ?? 'no client'];
  return `${m.name} (${details.join(', ')}) · #${id}`;
}

function DuplicateGroup({
  kind,
  group,
}: {
  readonly kind: 'clients' | 'contacts';
  readonly group: DuplicateGroupDTO;
}): JSX.Element {
  const toast = useToast();
  const merge = useMerge();
  const [targetId, setTargetId] = useState<number>(group.ids[0] as number);
  const [preview, setPreview] = useState<MergePreviewDTO | null>(null);

  const sourceIds = group.ids.filter((id) => id !== targetId);
  const label = (id: number): string => memberLabel(kind, group.members ?? [], id);

  async function askPreview(): Promise<void> {
    try {
      const out = await merge.mutateAsync({ kind, targetId, sourceIds });
      setPreview(out.preview);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not build the merge preview'));
    }
  }

  async function confirmMerge(): Promise<void> {
    try {
      await merge.mutateAsync({ kind, targetId, sourceIds, confirm: true });
      setPreview(null);
      toast.success('Records merged');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not merge'));
    }
  }

  return (
    <li
      data-testid={`duplicate-group-${kind}-${group.value}`}
      className="rounded-lg border border-[var(--color-border)] bg-white p-3"
    >
      <p className="text-sm font-medium">{group.value}</p>
      <p className="text-xs text-slate-500">{REASON_LABEL[group.reason]}</p>

      <fieldset className="mt-2">
        <legend className="text-xs text-slate-600">Keep this record:</legend>
        <div className="flex flex-wrap gap-3">
          {group.ids.map((id) => (
            <label
              key={id}
              className="flex items-center gap-1 text-sm"
              data-testid={`merge-member-${kind}-${id}`}
            >
              <input
                type="radio"
                name={`merge-${kind}-${group.value}`}
                checked={id === targetId}
                data-testid={`merge-target-${kind}-${id}`}
                onChange={() => {
                  setTargetId(id);
                  // The preview belonged to the PREVIOUS survivor; keeping it visible
                  // would invite confirming a merge the user never previewed.
                  setPreview(null);
                }}
              />
              <span className="whitespace-pre">{label(id)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className={BTN_GHOST}
          disabled={merge.isPending || sourceIds.length === 0}
          data-testid={`merge-preview-${kind}-${group.value}`}
          onClick={() => void askPreview()}
        >
          What will change?
        </button>
        {preview ? (
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={merge.isPending}
            data-testid={`merge-confirm-${kind}-${group.value}`}
            onClick={() => void confirmMerge()}
          >
            Merge
          </button>
        ) : null}
      </div>

      {preview ? (
        <dl
          data-testid={`merge-preview-body-${kind}-${group.value}`}
          className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md bg-slate-50 p-2 text-xs"
        >
          <dt className="text-slate-600">Kept</dt>
          <dd className="whitespace-pre-wrap">{label(preview.target_id)}</dd>
          <dt className="text-slate-600">Deleted</dt>
          <dd className="whitespace-pre-wrap">{preview.source_ids.map(label).join('; ')}</dd>
          <dt className="text-slate-600">Projects re-linked</dt>
          <dd>{preview.projects_relinked}</dd>
          {kind === 'clients' ? (
            <>
              <dt className="text-slate-600">Contacts moved</dt>
              <dd>{preview.contacts_moved}</dd>
            </>
          ) : null}
          <dt className="text-slate-600">Fields filled</dt>
          <dd>{preview.fields_filled.length > 0 ? preview.fields_filled.join(', ') : '—'}</dd>
        </dl>
      ) : null}
    </li>
  );
}
